/**
 * Client side of the Excalidraw board protocol (eval/MIRO_PLAN.md):
 *
 *   onChange ──► VersionLedger.changed (only elements whose version moved past
 *                what the server holds) ──► throttled {type:'update'}
 *   update / init ──► restoreElements ──► reconcileElements (higher version
 *                wins, equal → lower versionNonce, tombstones kept, the element
 *                being edited locally is kept) ──► updateScene(captureUpdate
 *                NEVER) so remote edits never enter undo history
 *
 * Every (re)connect receives a full `init`: the ledger is reset to the
 * server's state, the scene is reconciled with it, and whatever local edits
 * the server lacks (made offline, or lost in a dropped socket) are re-sent —
 * no edit is lost across a network drop.
 *
 * Presence: `users` / `pointer` / `idle` feed Excalidraw's collaborators API
 * (named coloured cursors, laser trails, the user list). Following a user
 * pans the viewport to keep their cursor in view.
 *
 * Images: new image files are uploaded to /api/boards/{id}/assets/ and
 * announced once with {type:'file'}; remote files (init.files / file
 * messages) are fetched and handed to `addFiles`.
 */

import { CaptureUpdateAction, newElementWith, reconcileElements, restoreElements } from '@excalidraw/excalidraw';
import type { OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { RemoteExcalidrawElement } from '@excalidraw/excalidraw/data/reconcile';
import type { AppState, BinaryFileData, BinaryFiles, Collaborator, ExcalidrawImperativeAPI, SocketId } from '@excalidraw/excalidraw/types';
import { BoardSocket, type ConnState } from './BoardSocket';
import type { IdleState, InitMsg, PointerMsg, ServerMsg, WireElement, WireFile, WireUser } from './protocol';
import { batches, VersionLedger } from './versions';

export interface PresenceUser extends WireUser {
  state: IdleState;
  isSelf: boolean;
}

export interface CollabOptions {
  boardId: string;
  user: { id: string; name: string };
  socketUrl(token: string): string;
  getToken(): string | null;
  refreshToken(): Promise<string | null>;
  uploadAsset(file: Blob, filename: string): Promise<{ url: string; mime?: string }>;
  readOnly?: boolean;
  /** Elements to seed into an empty board on the first init (deterministic ids). */
  buildSeed?(): Promise<readonly OrderedExcalidrawElement[] | null> | readonly OrderedExcalidrawElement[] | null;
  onConnState?(s: ConnState): void;
  onUsers?(users: PresenceUser[]): void;
  onInit?(info: { first: boolean; seeded: boolean; elementCount: number }): void;
  onRemoteApplied?(elements: readonly { id: string; version: number }[]): void;
  onError?(message: string): void;
  createSocket?: ConstructorParameters<typeof BoardSocket>[0]['createSocket'];
}

const SEND_THROTTLE_MS = 50;
const POINTER_THROTTLE_MS = 40;
const IDLE_AFTER_MS = 60_000;
const MAX_ASSET_BYTES = 15 * 1024 * 1024;

type AnyEl = OrderedExcalidrawElement & { fileId?: string | null; status?: string };

export class ExcalidrawCollab {
  readonly ledger = new VersionLedger();
  readonly socket: BoardSocket;
  readonly stats = { sent: 0, received: 0, updatesOut: 0, updatesIn: 0, inits: 0 };
  private initialised = false;
  private gotInitThisConnection = false;
  private selfSocketId: string | null = null;
  private users = new Map<string, PresenceUser>();
  private pointers = new Map<string, Pick<PointerMsg, 'pointer' | 'button' | 'selectedElementIds' | 'label'>>();
  private label = '';
  private serverFiles = new Map<string, WireFile>();
  /** Files this client uploaded (resent on init if the server forgot them). */
  private uploaded = new Map<string, WireFile>();
  private uploading = new Set<string>();
  private loadingFiles = new Set<string>();
  private failedFiles = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFlush = 0;
  private pointerTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingPointer: PointerMsg['pointer'] | null = null;
  private pendingButton: 'up' | 'down' = 'up';
  private lastPointerSent = 0;
  private idleState: IdleState = 'active';
  private lastActivity = Date.now();
  private idleTimer: ReturnType<typeof setInterval> | null = null;
  private disposers: (() => void)[] = [];
  private readOnly: boolean;
  private stopped = false;

  constructor(
    private readonly xapi: ExcalidrawImperativeAPI,
    private readonly o: CollabOptions,
  ) {
    this.readOnly = !!o.readOnly;
    this.socket = new BoardSocket({
      url: o.socketUrl,
      getToken: o.getToken,
      refreshToken: o.refreshToken,
      createSocket: o.createSocket,
      onMessage: (m) => this.onMessage(m),
      onState: (s) => {
        if (s !== 'open') this.gotInitThisConnection = false;
        o.onConnState?.(s);
      },
    });
  }

  get connState(): ConnState {
    return this.socket.connState;
  }
  get isReady(): boolean {
    return this.initialised;
  }
  get presence(): PresenceUser[] {
    return [...this.users.values()];
  }

  setReadOnly(v: boolean): void {
    this.readOnly = v;
  }

  start(): void {
    this.socket.start();
    const onOnline = () => this.socket.reconnectNow();
    const onOffline = () => this.socket.simulateDrop();
    const onActivity = () => this.markActive();
    const onVis = () => this.evaluateIdle();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pointermove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity);
    document.addEventListener('visibilitychange', onVis);
    this.idleTimer = setInterval(() => this.evaluateIdle(), 5000);
    this.disposers.push(() => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pointermove', onActivity);
      window.removeEventListener('keydown', onActivity);
      document.removeEventListener('visibilitychange', onVis);
    });
  }

  /** Send anything pending, then close. */
  stop(): void {
    if (this.stopped) return;
    this.flush();
    this.stopped = true;
    this.socket.stop();
    if (this.flushTimer) clearTimeout(this.flushTimer);
    if (this.pointerTimer) clearTimeout(this.pointerTimer);
    if (this.idleTimer) clearInterval(this.idleTimer);
    this.disposers.forEach((d) => d());
    this.disposers = [];
  }

  // ---- inbound ---------------------------------------------------------------

  private onMessage(m: ServerMsg): void {
    if (this.stopped) return;
    this.stats.received++;
    switch (m.type) {
      case 'init':
        void this.handleInit(m);
        break;
      case 'update':
        this.stats.updatesIn++;
        if (Array.isArray(m.elements) && m.elements.length) this.applyRemote(m.elements);
        break;
      case 'users':
        this.setUsers(m.users ?? []);
        break;
      case 'pointer':
        this.handlePointer(m);
        break;
      case 'idle': {
        const u = this.users.get(m.socketId);
        if (u) {
          u.state = m.state;
          this.publishPresence();
        }
        break;
      }
      case 'file':
        this.serverFiles.set(m.fileId, { url: m.url, mimeType: m.mimeType });
        void this.loadMissingFiles();
        break;
      case 'error':
        if (m.code === 'readonly') {
          this.readOnly = true;
          break;
        }
        this.o.onError?.(m.message ?? m.error ?? `Sync error${m.code ? ` (${m.code})` : ''}`);
        break;
    }
  }

  private async handleInit(m: InitMsg): Promise<void> {
    this.stats.inits++;
    const first = !this.initialised;
    this.selfSocketId = m.socketId ?? this.selfSocketId;
    if (m.role === 'viewer') this.readOnly = true;
    const server = Array.isArray(m.elements) ? m.elements : [];
    this.ledger.resetFromServer(server);
    if (server.length) this.applyRemote(server, { markLedger: false });
    for (const [id, f] of Object.entries(m.files ?? {})) this.serverFiles.set(id, f);
    // Files we uploaded that the server doesn't list (e.g. announced while the socket was dropping).
    const resend = [...this.uploaded].filter(([id]) => !(m.files && id in m.files));
    this.setUsers(m.users ?? []);
    this.initialised = true;
    this.gotInitThisConnection = true;
    for (const [fileId, f] of resend) this.socket.send({ type: 'file', fileId, url: f.url, mimeType: f.mimeType });

    let seeded = false;
    if (first && server.length === 0 && !this.readOnly && this.o.buildSeed) {
      const local = this.xapi.getSceneElementsIncludingDeleted();
      if (local.length === 0) {
        const seed = await this.o.buildSeed();
        if (seed && seed.length && this.xapi.getSceneElementsIncludingDeleted().length === 0) {
          this.xapi.updateScene({ elements: seed, captureUpdate: CaptureUpdateAction.NEVER });
          seeded = true;
        }
      }
    }
    void this.loadMissingFiles();
    this.flush();
    this.o.onInit?.({ first, seeded, elementCount: this.xapi.getSceneElementsIncludingDeleted().length });
  }

  /** Reconcile remote elements into the scene (never enters undo history). */
  private applyRemote(remote: readonly WireElement[], opts: { markLedger?: boolean } = {}): void {
    const local = this.xapi.getSceneElementsIncludingDeleted();
    const restored = restoreElements(remote as never, null) as unknown as RemoteExcalidrawElement[];
    const reconciled = reconcileElements(local, restored, this.xapi.getAppState());
    this.xapi.updateScene({ elements: reconciled, captureUpdate: CaptureUpdateAction.NEVER });
    if (opts.markLedger !== false) {
      const cur = new Map(reconciled.map((e) => [e.id, e]));
      this.ledger.markReceived(remote, cur);
    }
    this.o.onRemoteApplied?.(restored);
    if (restored.some((e) => e.type === 'image')) void this.loadMissingFiles();
  }

  private setUsers(list: WireUser[]): void {
    const next = new Map<string, PresenceUser>();
    for (const u of list) {
      if (!u || !u.socketId) continue;
      const prev = this.users.get(u.socketId);
      next.set(u.socketId, { ...u, state: prev?.state ?? 'active', isSelf: this.isSelf(u) });
    }
    for (const id of [...this.pointers.keys()]) if (!next.has(id)) this.pointers.delete(id);
    this.users = next;
    this.publishPresence();
  }

  private isSelf(u: { socketId: string; userId: string }): boolean {
    if (this.selfSocketId) return u.socketId === this.selfSocketId;
    return String(u.userId) === String(this.o.user.id);
  }

  private handlePointer(m: PointerMsg): void {
    if (!m.socketId || this.isSelf(m)) return;
    if (!this.users.has(m.socketId)) {
      this.users.set(m.socketId, { socketId: m.socketId, userId: m.userId, userName: m.userName, color: m.color, state: 'active', isSelf: false });
    } else {
      const u = this.users.get(m.socketId)!;
      if (u.state !== 'active') u.state = 'active';
    }
    this.pointers.set(m.socketId, { pointer: m.pointer, button: m.button, selectedElementIds: m.selectedElementIds, label: typeof m.label === 'string' ? m.label.slice(0, 80) : undefined });
    this.publishPresence(false);
    const follow = this.xapi.getAppState().userToFollow;
    if (follow && follow.socketId === m.socketId && m.pointer) this.keepInView(m.pointer.x, m.pointer.y);
  }

  /** Follow mode: pan so the followed user's cursor stays inside the middle 60 % of the view. */
  keepInView(x: number, y: number, force = false): void {
    const st = this.xapi.getAppState();
    const z = st.zoom.value;
    const w = st.width / z;
    const h = st.height / z;
    const left = -st.scrollX;
    const top = -st.scrollY;
    const inside = x > left + w * 0.2 && x < left + w * 0.8 && y > top + h * 0.2 && y < top + h * 0.8;
    if (inside && !force) return;
    this.xapi.updateScene({ appState: { scrollX: w / 2 - x, scrollY: h / 2 - y }, captureUpdate: CaptureUpdateAction.NEVER });
  }

  /** Pan to a collaborator's last known cursor (used when starting to follow). */
  jumpTo(socketId: string): boolean {
    const p = this.pointers.get(socketId)?.pointer;
    if (!p) return false;
    this.keepInView(p.x, p.y, true);
    return true;
  }

  private publishPresence(includeList = true): void {
    const collaborators = new Map<SocketId, Collaborator>();
    for (const u of this.users.values()) {
      const p = this.pointers.get(u.socketId);
      collaborators.set(u.socketId as SocketId, {
        id: String(u.userId),
        socketId: u.socketId as SocketId,
        username: !u.isSelf && p?.label ? `${u.userName} · ${p.label}` : u.userName,
        color: { background: u.color, stroke: u.color },
        isCurrentUser: u.isSelf,
        userState: u.state as Collaborator['userState'],
        ...(u.isSelf || !p ? {} : { pointer: p.pointer, button: p.button, selectedElementIds: p.selectedElementIds as AppState['selectedElementIds'] }),
      });
    }
    this.xapi.updateScene({ collaborators });
    if (includeList) this.o.onUsers?.(this.presence);
  }

  // ---- outbound --------------------------------------------------------------

  /** Excalidraw onChange → schedule a throttled send; pick up new image files. */
  handleChange(files: BinaryFiles): void {
    if (this.stopped) return;
    this.maybeUploadFiles(files);
    this.scheduleFlush();
  }

  scheduleFlush(): void {
    if (this.flushTimer) return;
    const wait = Math.max(0, SEND_THROTTLE_MS - (Date.now() - this.lastFlush));
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, wait);
  }

  /** Send every element whose version moved past what the server holds. */
  flush(): number {
    this.lastFlush = Date.now();
    if (this.readOnly || !this.initialised || !this.gotInitThisConnection || !this.socket.isOpen) return 0;
    const changed = this.ledger.changed(this.xapi.getSceneElementsIncludingDeleted());
    if (!changed.length) return 0;
    let n = 0;
    for (const chunk of batches(changed, 500)) {
      if (!this.socket.send({ type: 'update', elements: chunk as unknown as WireElement[] })) break;
      this.ledger.markSent(chunk);
      this.stats.sent += chunk.length;
      this.stats.updatesOut++;
      n += chunk.length;
    }
    return n;
  }

  /** What we're doing, shown next to our cursor on collaborators' screens ('' = nothing). */
  setLabel(label: string): void {
    const next = label.slice(0, 80);
    if (next === this.label) return;
    this.label = next;
    if (this.pendingPointer) this.emitPointer();
  }

  /** Excalidraw onPointerUpdate → throttled pointer message. */
  sendPointer(pointer: { x: number; y: number; tool: 'pointer' | 'laser' }, button: 'up' | 'down'): void {
    this.markActive();
    this.pendingPointer = pointer;
    const buttonChanged = button !== this.pendingButton;
    this.pendingButton = button;
    const elapsed = Date.now() - this.lastPointerSent;
    if (buttonChanged || elapsed >= POINTER_THROTTLE_MS) {
      this.emitPointer();
      return;
    }
    if (!this.pointerTimer) this.pointerTimer = setTimeout(() => this.emitPointer(), POINTER_THROTTLE_MS - elapsed);
  }

  private emitPointer(): void {
    if (this.pointerTimer) clearTimeout(this.pointerTimer);
    this.pointerTimer = null;
    const p = this.pendingPointer;
    if (!p) return;
    this.lastPointerSent = Date.now();
    const sel = this.xapi.getAppState().selectedElementIds;
    this.socket.send({ type: 'pointer', pointer: p, button: this.pendingButton, selectedElementIds: sel as Record<string, true>, ...(this.label ? { label: this.label } : {}) });
  }

  private markActive(): void {
    this.lastActivity = Date.now();
    if (this.idleState !== 'active') this.evaluateIdle();
  }

  private evaluateIdle(): void {
    const next: IdleState =
      typeof document !== 'undefined' && document.hidden ? 'away' : Date.now() - this.lastActivity > IDLE_AFTER_MS ? 'idle' : 'active';
    if (next === this.idleState) return;
    this.idleState = next;
    this.socket.send({ type: 'idle', state: next });
  }

  // ---- files -----------------------------------------------------------------

  private maybeUploadFiles(files: BinaryFiles): void {
    if (this.readOnly) return;
    const els = this.xapi.getSceneElements() as readonly AnyEl[];
    for (const e of els) {
      if (e.type !== 'image' || !e.fileId) continue;
      const id = e.fileId;
      if (this.serverFiles.has(id) || this.uploaded.has(id) || this.uploading.has(id) || this.failedFiles.has(id)) continue;
      const f = files[id];
      if (!f?.dataURL) continue;
      void this.upload(f);
    }
  }

  private async upload(f: BinaryFileData): Promise<void> {
    this.uploading.add(f.id);
    try {
      const blob = await (await fetch(f.dataURL)).blob();
      if (blob.size > MAX_ASSET_BYTES) throw new Error('Image is larger than 15 MB');
      const ext = (f.mimeType.split('/')[1] ?? 'bin').replace('svg+xml', 'svg').replace('jpeg', 'jpg');
      const asset = await this.o.uploadAsset(new Blob([blob], { type: f.mimeType }), `${f.id.slice(0, 40)}.${ext}`);
      const wf: WireFile = { url: asset.url, mimeType: asset.mime ?? f.mimeType };
      this.uploaded.set(f.id, wf);
      this.serverFiles.set(f.id, wf);
      this.socket.send({ type: 'file', fileId: f.id, url: wf.url, mimeType: wf.mimeType });
      this.setImageStatus(f.id, 'saved');
    } catch (e) {
      this.failedFiles.add(f.id);
      this.setImageStatus(f.id, 'error');
      this.o.onError?.(`Image upload failed: ${(e as Error).message}`);
    } finally {
      this.uploading.delete(f.id);
    }
  }

  private setImageStatus(fileId: string, status: 'saved' | 'error'): void {
    const els = this.xapi.getSceneElementsIncludingDeleted() as readonly AnyEl[];
    let touched = false;
    const next = els.map((e) => {
      if (e.type === 'image' && e.fileId === fileId && e.status !== status) {
        touched = true;
        return newElementWith(e as never, { status } as never) as unknown as AnyEl;
      }
      return e;
    });
    if (touched) {
      this.xapi.updateScene({ elements: next, captureUpdate: CaptureUpdateAction.NEVER });
      this.scheduleFlush();
    }
  }

  private async loadMissingFiles(): Promise<void> {
    const have = this.xapi.getFiles();
    const needed = new Set<string>();
    for (const e of this.xapi.getSceneElements() as readonly AnyEl[]) if (e.type === 'image' && e.fileId) needed.add(e.fileId);
    const todo = [...needed].filter((id) => !have[id] && this.serverFiles.has(id) && !this.loadingFiles.has(id));
    await Promise.all(
      todo.map(async (id) => {
        const f = this.serverFiles.get(id)!;
        this.loadingFiles.add(id);
        try {
          const res = await fetch(f.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const dataURL = await blobToDataURL(blob);
          this.xapi.addFiles([{ id: id as BinaryFileData['id'], dataURL: dataURL as BinaryFileData['dataURL'], mimeType: f.mimeType as BinaryFileData['mimeType'], created: Date.now(), lastRetrieved: Date.now() }]);
        } catch (e) {
          this.o.onError?.(`Could not load image: ${(e as Error).message}`);
        } finally {
          this.loadingFiles.delete(id);
        }
      }),
    );
  }
}

export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
